<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import type { ModalData } from "@comp-lib/stores/modalStore";
import CustomButton from "./CustomButton.vue";

const props = defineProps<{
  isModalOpen: boolean;
  modalData: ModalData;
}>();

const emit = defineEmits(["@close", "@confirm"]);

const overlayEl = ref<HTMLElement | null>(null);

function checkClickTarget(e: MouseEvent) {
  if (!props.isModalOpen) return;
  e.stopPropagation();
  if (e.target === overlayEl.value) {
    emit("@close");
  }
}

onMounted(() => {
  document.body.addEventListener("click", checkClickTarget);
});
onBeforeUnmount(() => {
  document.body.removeEventListener("click", checkClickTarget);
});

defineExpose({ checkClickTarget });
</script>

<template>
  <Transition name="fade">
    <div v-if="props.isModalOpen" ref="overlayEl" class="modal-overlay">
      <div :class="['modal-body', props.modalData.size]">
        <button class="modal-close" @click="emit('@close')">
          <FontAwesomeIcon :icon="faXmark" />
        </button>
        <div class="modal-content">
          <span v-if="props.modalData.title" class="modal-title">{{ props.modalData.title }}</span>
          
          <component
            :is="props.modalData.component || 'div'"
            v-if="props.modalData.component"
            v-bind="props.modalData.componentProps"
            v-on="Object.keys(props.modalData.componentEvents ?? {}).reduce((acc, key) => ({
              ...acc,
              [key]: ($event: Event) => props.modalData.componentEvents?.[key]?.($event)
            }), {})"
          />

          <!-- NOTE: Using v-html is safe here since its value isn't user-modifiable -->
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-else class="modal-innertext" v-html="props.modalData.textContent" />

          <div v-if="props.modalData.confirmMethod" class="modal-action">
            <CustomButton
              label="Cancel"
              btnStyle="secondary"
              size="md"
              expand
              data-test-cancel-btn
              @click="emit('@close')"
            />
            <CustomButton
              :label="props.modalData.confirmText || 'Continue'"
              size="md"
              expand
              data-test-confirm-btn
              :destructive="props.modalData.destructive"
              @click="emit('@confirm')"
            />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss">
.modal {
  &-overlay {
    z-index: 15;
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    padding: 30px 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(map.get(v.$gray, "900"), 0.5);
    scrollbar-gutter: stable;
    overflow: auto;
  }
  &-body {
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 100%;
    max-height: 100%;
    padding: 10px 20px;
    border-radius: map.get(v.$border-radius, "md");
    background: white;
    @media (max-width: map.get(v.$breakpoints, "sm")) {
      padding: 15px;
    }
    &.sm {
      width: 460px;
    }
    &.md {
      width: 600px;
    }
    &.lg {
      width: 720px;
    }
  }
  &-title {
    margin: 0 2rem 0 0;
    font-size: 1.5rem;
    font-weight: map.get(v.$font-weight, "medium");
    line-height: 120%;
    @media (max-width: map.get(v.$breakpoints, "sm")) {
      text-align: center;
    }
  }
  &-content {
    overflow-y: auto;
    width: 100%;
    padding: 10px;
  }
  &-innertext {
    margin-top: 20px;
    color: map.get(v.$gray, "500");
    line-height: 140%;
    @media (max-width: map.get(v.$breakpoints, "sm")) {
      text-align: center;
    }
  }
  &-close {
    position: absolute;
    font-size: 1.5rem;
    line-height: 0;
    top: 20px;
    right: 20px;
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 0 5px 5px white;
    padding: 0;
    color: map.get(v.$gray, "400");
    border: none;
    transition: .2s;
    cursor: pointer;
    &:hover {
      color: #000;
    }
    @media (max-width: map.get(v.$breakpoints, "sm")) {
      top: 15px;
      right: 15px;
    }
  }
  &-action {
    display: flex;
    gap: 10px;
    margin-top: 40px;
    @media (max-width: map.get(v.$breakpoints, "sm")) {
      flex-wrap: wrap;
    }
  }
}
</style>